import {
  Component,
  effect,
  inject,
  input,
  signal,
  ViewChild,
  WritableSignal, output,
} from '@angular/core';
import {
  SynButtonComponent,
  SynComboboxComponent,
  SynIconComponent,
  SynOptionComponent, SynSpinnerComponent,
} from '@synergy-design-system/angular';
import { ServicePortalApi } from '../../api/api';
import { debounceTime } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { UserReference, UserSearchResult } from '../../api/types';

@Component({
  selector: 'app-user-search',
  template: `
    <form class="flex-row gap-1" (submit)="onSubmit(); $event.preventDefault()">
      <syn-combobox
        placeholder="Search for a user or type a username"
        i18n-placeholder="@@search-user-or-type-username"
        [(value)]="value"
        [clearable]="true"
        (synInputEvent)="input.nativeElement.setCustomValidity('')"
        class="flex-grow-1"
        #input
      >
        <syn-icon name="search_fill" slot="prefix" />
        @if (loading()) {
          <syn-spinner slot="suffix" />
        }

        @for (user of userOptions(); track user.id) {
          <syn-option [value]="ID_PREFIX + user.id">
            {{ displayName(user) }}
          </syn-option>
        }
      </syn-combobox>
      <syn-button
        variant="filled" type="submit"
        [disabled]="disabled() || value().length === 0"
      >
        <syn-icon name="add_fill" slot="prefix" />
        <span>{{ actionText() }}</span>
      </syn-button>
    </form>
  `,
  styles: [],
  imports: [
    SynComboboxComponent,
    SynOptionComponent,
    SynIconComponent,
    SynButtonComponent,
    SynSpinnerComponent,
  ],
})
export class UserSearchComponent {
  api = inject(ServicePortalApi);

  ID_PREFIX = 'sickid:';

  value = signal('');
  disabled = input(false);
  actionText = input('');

  userSelected = output<UserReference>();

  debouncedSearch = toSignal(toObservable(this.value).pipe(
    debounceTime(400),
  ));
  userOptions: WritableSignal<UserSearchResult[]> = signal([]);
  loading = signal(false);

  @ViewChild(SynComboboxComponent) input!: SynComboboxComponent;

  constructor() {
    effect(() => {
      const search = this.debouncedSearch();
      if (!search) {
        this.userOptions.set([]);
        return;
      }
      if (search.startsWith(this.ID_PREFIX)) {
        // If a user was selected, we need to keep the options alive
        // Otherwise, the combo box will display the raw ID value
        return;
      }

      const trimmedSearch = search.trim();
      if (trimmedSearch.length < 3) {
        this.userOptions.set([]);
        return;
      }

      this.loading.set(true);
      this.api.searchUsers(trimmedSearch, (result) => {
        this.userOptions.set(result.users);
        this.loading.set(false);
      });
    });
  }

  public setUserDoesNotExistError() {
    const userExistsError =
      $localize`:@@error-user-does-not-exist:A user with the given username does not exist.`;
    this.setError(userExistsError);
  }

  public setError(error: string) {
    this.input.nativeElement.setCustomValidity(error);
    if (error.length > 0) {
      this.input.nativeElement.reportValidity();
    }
  }

  public clear() {
    this.input.nativeElement.value = '';
    this.userOptions.set([]);
  }

  public setValue(value: string) {
    this.value.set(value);
  }

  displayName(user: UserSearchResult) {
    const name = (user.firstName + ' ' + user.lastName + ' ').trimStart();
    if (name.length === 0) {
      return user.username;
    }
    return name + '(' + user.username + ')';
  }

  onSubmit() {
    // We need to get the value directly from the element
    // Using the debounced or signal value sometimes gives an outdated value
    const value = this.input.nativeElement.value;
    if (typeof value !== 'string') {
      console.warn('Unexpected type of combo box value: ' + value);
      return;
    }
    if (value.startsWith(this.ID_PREFIX)) {
      const id = value.substring(this.ID_PREFIX.length);
      this.userSelected.emit({ id });
    } else if (value.length > 0) {
      this.userSelected.emit({ username: value });
    }
  }
}
